import React, { useEffect, useState } from "react";
import axios from "axios";
import { History, Loader2, Undo2 } from "lucide-react";
import { Button } from "./ui/button";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/owner-actions`;
const opts = { withCredentials: true };
const when = (iso) => new Date(iso).toLocaleString();

const ChangeRow = ({ c, busy, onUndo }) => (
  <div className="flex items-center justify-between gap-3 rounded border border-slate-100 p-2 text-xs" data-testid={`history-row-${c.batch_id}`}>
    <div>
      <div className="font-medium text-slate-900">{when(c.at)} · {c.count} item(s)</div>
      <div className="text-slate-500 truncate max-w-md" title={c.keys.join(", ")}>{c.keys.join(", ")}</div>
      {c.undone_at && <div className="text-emerald-700">Undone {when(c.undone_at)}{c.pending ? ` · ${c.pending} still not undone` : ""}</div>}
    </div>
    <Button size="sm" variant="outline" disabled={busy || !c.pending} onClick={() => onUndo(c)} data-testid={`history-undo-btn-${c.batch_id}`}>
      <Undo2 className="h-3 w-3 mr-1" /> {!c.pending ? "Undone" : c.undone_at ? "Try undo on remaining" : "Undo"}
    </Button>
  </div>
);

export const ChangeHistory = ({ kind, onUndone, refreshKey }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!refreshKey) return;
    setMsg(null);
    axios.get(`${BASE}/history/${kind}`, opts).then((r) => setRows(r.data.changes)).catch(() => {});
  }, [refreshKey, kind]);

  const load = async () => {
    setBusy(true);
    try { setRows((await axios.get(`${BASE}/history/${kind}`, opts)).data.changes); }
    catch (e) { setMsg(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const undo = async (c) => {
    if (!window.confirm(`Change back ${c.pending} item(s) from ${when(c.at)}?`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = (await axios.post(`${BASE}/undo/${c.batch_id}`, {}, opts)).data;
      const skipped = r.skipped.map((s) => `${s.key} (${s.reason})`).join("; ");
      setMsg(`${r.restored.length} item(s) changed back.${skipped ? ` Not changed: ${skipped}.` : ""}`);
      onUndone && onUndone();
      setRows((await axios.get(`${BASE}/history/${kind}`, opts)).data.changes);
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-slate-100 pt-3 space-y-2" data-testid={`history-${kind}`}>
      <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid={`history-show-btn-${kind}`}>
        {busy && !rows ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <History className="h-4 w-4 mr-1" />} Past changes and undo
      </Button>
      {rows && rows.length === 0 && <p className="text-xs text-slate-500" data-testid={`history-empty-${kind}`}>No past changes yet.</p>}
      {rows && rows.map((c) => <ChangeRow key={c.batch_id} c={c} busy={busy} onUndo={undo} />)}
      {msg && <p className="text-sm text-slate-800" data-testid={`history-msg-${kind}`}>{msg}</p>}
    </div>
  );
};
